import { useCallback, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

// AUTH TOKEN START
import jwt_decode from 'jwt-decode'
import { ls } from 'utils'
import { LSKEY_TOKEN } from 'constants/app'
// AUTH TOKEN END

import { updateMemberDetailAPI } from 'api/members'
import { MerchantLookupTypes } from 'types/merchanTxTypes'
import {
  MemberDetailUpdateFormTypes,
  MemberDetailUpdateFormSubmitValuesTypes,
  MemberDetailUpdateFormSubmitActionsTypes,
} from 'types/member'
import { MemberDetail } from 'types/member'
import useMemberStore from 'store/member'

import { isQSRHClient } from 'setups/controller'
const projectClient = process.env.REACT_APP_PROJ

const PURCHASETXINITVAL: MemberDetailUpdateFormTypes = {
  firstName: '',
  lastName: '',
  birthDate: '',
  genderId: '1',
  email: '',
  mobile: '',
  enrollmentTraderId: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  suburb: '',
  state: '',
  city: '',
  postCode: '',
  country: '',
  channelEmail: null,
  channelSMS: null,
  traderRankingId: null,
  comments: '',
}

export const useProfileUpdate = (
  traderId: number,
  onCloseModal: () => void
) => {
  // AUTH TOKEN START
  const authToken = ls.get(LSKEY_TOKEN)
  const decodeAuthToken: { username: string } = jwt_decode(String(authToken))
  const authTokenUsername = decodeAuthToken?.username || ''
  // AUTH TOKEN END
  // STATES
  const [isLoading, isLoadingSet] = useState<boolean>(false)
  const [purchaseTxMerchant, purchaseTxMerchantSet] = useState<
    MerchantLookupTypes[]
  >([])
  const member = useMemberStore((state) => state.member)
  const getMemberStore = useMemberStore((state) => state.getMember)
  const getMemberRankings = useMemberStore((state) => state.getMemberRankings)
  const [initialValues, initialValuesSet] =
    useState<MemberDetailUpdateFormTypes>({
      ...PURCHASETXINITVAL,
      firstName: member?.firstName,
      lastName: member?.lastName,
      birthDate: member?.birthDate,
      genderId: member?.genderId,
      email: member?.email,
      mobile: member?.mobile,
      enrollmentTraderId: member?.enrollmentTraderId,
      address1: member?.address1,
      address2: member?.address2,
      address3: member?.address3,
      address4: member?.address4,
      suburb: member?.suburb,
      state: member?.state,
      city: member?.city,
      postCode: member?.postCode,
      country: member?.country,
      channelEmail: member?.channelEmail,
      channelSMS: member?.channelSMS,
      traderRankingId: member?.rankingId,
      comments: member?.comments,
    })
  const queryClient = useQueryClient()

  useEffect(() => {
    getMemberRankings()
  }, [])

  const handleMerchantSet = useCallback(
    async (merchant: MerchantLookupTypes[]) => {
      try {
        purchaseTxMerchantSet(merchant)
        toast.success('Merchant Selected')
      } catch (error) {
        toast.error('Something went wrong.')
      }
    },
    [purchaseTxMerchantSet]
  )

  const handleSubmitProfileUpdate = useCallback(
    async (
      values: MemberDetailUpdateFormSubmitValuesTypes,
      actions: MemberDetailUpdateFormSubmitActionsTypes
    ) => {
      isLoadingSet(true)
      try {
        if (!isQSRHClient(projectClient)) {
          const transformedValues = {
            ...values,
            address1:
              values.address1 === '' && member?.address1 ? '|REMOVED|' : values.address1,
            address2:
              values.address2 === '' && member?.address2 ? '|REMOVED|' : values.address2,
            address3:
              values.address3 === '' && member?.address3 ? '|REMOVED|' : values.address3,
            address4:
              values.address4 === '' && member?.address4 ? '|REMOVED|' : values.address4,
            suburb:
              values.suburb === '' && member?.suburb ? '|REMOVED|' : values.suburb,
            city:
              values.city === '' && member?.city ? '|REMOVED|' : values.city,
            state:
              values.state === '' && member?.state ? '|REMOVED|' : values.state,
            postCode:
              values.postCode === '' && member?.postCode ? '|REMOVED|' : values.postCode,
          }
          await updateMemberDetailAPI(transformedValues, traderId)
        } else {
          await updateMemberDetailAPI(values, traderId)
        }
        await getMemberStore(String(traderId))
        initialValuesSet(values)
        queryClient.invalidateQueries({ queryKey: ['useMemberProfiles'] })
        toast.success('Member update successful.')
        onCloseModal()
      } catch (error) {
        const message = JSON.parse(JSON.stringify(error))
        toast.error(message.outcomeUserMessage)
      }
      isLoadingSet(false)
    },
    [isLoadingSet, getMemberStore, initialValuesSet, member, traderId, onCloseModal, queryClient]
  )

  const handleClearProfileUpdate = useCallback(() => {
    initialValuesSet({ ...PURCHASETXINITVAL })
    toast.success('Filter successfully cleared.')
  }, [initialValuesSet])

  return {
    member,
    isLoading,
    initialValues,
    handleMerchantSet,
    handleSubmitProfileUpdate,
    handleClearProfileUpdate,
  }
}
